import { ImageFrames, StoreTheme } from '@/types/store';
import { Fonts, SecondaryFonts } from '../types/store';

// Color collection enum
export enum ColorCollection {
    Ritual = 'ritual',
    Heritage = 'heritage',
    Playground = 'playground',
    Stilness = 'stilness',
    Ember = 'ember',
    Signal = 'signal',
}

// Color collections
export const Colors = [
    {
        id: ColorCollection.Ritual,
        name: 'Ritual',
        description: 'Intense energy.',
        colors: {
            bg: {
                primary: '#171414',
                secondary: '#171414',
                tertiary: '#3c0d12',
                overlay: '#E9361E',
                input: '#1F1C1C',
            },
            fg: {
                primary: '#e9361e',
                secondary: '#000000',
                tertiary: '#2E2B2B',
                overlay: '#3C0D12',
                input: '#FFFFFF',
            },
            accent: {
                primary: '#e9361e',
                secondary: '#e9361e',
            },
        },
    },
    {
        id: ColorCollection.Heritage,
        name: 'Heritage',
        description: 'Editorial and elevated.',
        colors: {
            bg: {
                primary: '#2d5c4a',
                secondary: '#2d5c4a',
                tertiary: '#2a5041',
                overlay: '#F9F7F4',
                input: '#2A5041',
            },
            fg: {
                primary: '#f9f7f4',
                secondary: '#1e1e1e',
                tertiary: '#567B6C',
                overlay: '#2D5C4A',
                input: '#F9F7F4',
            },
            accent: {
                primary: '#c1a45f',
                secondary: '#c1a45f',
            },
        },
    },
    {
        id: ColorCollection.Ember,
        name: 'Ember',
        description: 'Bright base and vivid accents.',
        colors: {
            bg: {
                primary: '#f7d7d4',
                secondary: '#f2f0e5',
                tertiary: '#f7d7d4',
                overlay: '#f2f0e5',
                input: '#f7d7d4',
            },
            fg: {
                primary: '#ce2c1e',
                secondary: '#ffffff',
                tertiary: '#e7938b',
                overlay: '#ce2c1e',
                input: '#ce2c1e',
            },
            accent: {
                primary: '#ea5548',
                secondary: '#ea5548',
            },
        },
    },
    {
        id: ColorCollection.Playground,
        name: 'Playground',
        description: 'Vibrant and expressive.',
        colors: {
            bg: {
                primary: '#fdfcf9',
                secondary: '#fdfcf9',
                tertiary: '#f2ff90',
                overlay: '#F2FF90',
                input: '#FDFCF9',
            },
            fg: {
                primary: '#000000',
                secondary: '#fdfcf9',
                tertiary: '#CACAC7',
                overlay: '#000000',
                input: '#000000',
            },
            accent: {
                primary: '#f26caf',
                secondary: '#f26caf',
            },
        },
    },
    {
        id: ColorCollection.Stilness,
        name: 'Stilness',
        description: 'Light warmth.',
        colors: {
            bg: {
                primary: '#fdfcf9',
                secondary: '#fdfcf9',
                tertiary: '#f6f2ea',
                overlay: '#f6f2ea',
                input: '#FDFCF9',
            },
            fg: {
                primary: '#000000',
                secondary: '#fdfcf9',
                tertiary: '#CACAC7',
                overlay: '#000000',
                input: '#000000',
            },
            accent: {
                primary: '#b39b68',
                secondary: '#b39b68',
            },
        },
    },
    {
        id: ColorCollection.Signal,
        name: 'Signal',
        description: 'Direct and minimal.',
        colors: {
            bg: {
                primary: '#f5f4eb',
                secondary: '#f5f4eb',
                tertiary: '#e4e1ec',
                overlay: '#E4E1EC',
                input: '#E4E1EC',
            },
            fg: {
                primary: '#272729',
                secondary: '#f5f4eb',
                tertiary: '#CCCBC4',
                overlay: '#272729',
                input: '#272729',
            },
            accent: {
                primary: '#512aee',
                secondary: '#512aee',
            },
        },
    },
];

export const FontCollection = {
    [Fonts.BEBAS_NEUE]: {
        primary: Fonts.BEBAS_NEUE,
        secondary: SecondaryFonts.ROBOTO,
    },
    [Fonts.SPICY_RICE]: {
        primary: Fonts.SPICY_RICE,
        secondary: SecondaryFonts.WORK_SANS,
    },
    [Fonts.LORA]: {
        primary: Fonts.LORA,
        secondary: SecondaryFonts.INTER,
    },
    [Fonts.PLAYFAIR_DISPLAY]: {
        primary: Fonts.PLAYFAIR_DISPLAY,
        secondary: SecondaryFonts.INTER,
    },
    [Fonts.FJALLA_ONE]: {
        primary: Fonts.FJALLA_ONE,
        secondary: SecondaryFonts.ROBOTO,
    },
    [Fonts.BRIOLAGE_GROTESQUE]: {
        primary: Fonts.BRIOLAGE_GROTESQUE,
        secondary: SecondaryFonts.INTER,
    },
};

// Import theme preview images
import brim from '@/features/theme-editor/assets/brim.png';
import crest from '@/features/theme-editor/assets/crest.png';
import glint from '@/features/theme-editor/assets/glint.png';
import nova from '@/features/theme-editor/assets/nova.png';
import ode from '@/features/theme-editor/assets/ode.png';
import rung from '@/features/theme-editor/assets/rung.png';

// Theme definitions
export const Themes: {
    id: StoreTheme;
    name: string;
    colors: ColorCollection;
    imageFrames: ImageFrames;
    preview: string;
    fonts: (typeof FontCollection)[keyof typeof FontCollection];
}[] = [
    {
        id: StoreTheme.BRIM,
        name: 'Brim',
        colors: ColorCollection.Ritual,
        imageFrames: ImageFrames.SQUARE,
        fonts: FontCollection[Fonts.BEBAS_NEUE],
        preview: brim,
    },
    {
        id: StoreTheme.ODE,
        name: 'Ode',
        colors: ColorCollection.Stilness,
        imageFrames: ImageFrames.ARC,
        fonts: FontCollection[Fonts.LORA],
        preview: ode,
    },
    {
        id: StoreTheme.NOVA,
        name: 'Nova',
        colors: ColorCollection.Playground,
        imageFrames: ImageFrames.FLORAL,
        fonts: FontCollection[Fonts.SPICY_RICE],
        preview: nova,
    },

    {
        id: StoreTheme.CREST,
        name: 'Crest',
        colors: ColorCollection.Heritage,
        imageFrames: ImageFrames.ROUNDED_STRONG,
        fonts: FontCollection[Fonts.PLAYFAIR_DISPLAY],
        preview: crest,
    },
    {
        id: StoreTheme.RUNG,
        name: 'Rung',
        colors: ColorCollection.Signal,
        imageFrames: ImageFrames.ROUNDED_MEDIUM,
        fonts: FontCollection[Fonts.FJALLA_ONE],
        preview: rung,
    },
    {
        id: StoreTheme.GLINT,
        name: 'Glint',
        colors: ColorCollection.Ember,
        imageFrames: ImageFrames.ROUNDED_MEDIUM,
        fonts: FontCollection[Fonts.BRIOLAGE_GROTESQUE],
        preview: glint,
    },
];
